/**
 * Camera framing.
 *
 * The framing is computed from the live layout — including the band of the
 * viewport the glass chrome covers — rather than hard-coded per breakpoint.
 * Two things can be framed: the whole plinth, which every deck control has
 * to stay reachable within, or the focus region — the record, the tonearm and
 * the buttons in front of them — which the default view fills, letting the
 * plinth's edges run out of the picture.
 */

import * as THREE from 'three';
import { DIM, FOCUS } from '../geometry/dims.ts';
import { clamp } from '../lib/math.ts';

export type ViewName = 'top' | 'angle' | 'front' | 'arm';

/** What a view is fitted to. */
export type Fit = 'deck' | 'focus';

export interface ViewPreset {
  /** Polar angle from +Y. 0 looks straight down at the deck. */
  phi: number;
  /** Azimuth. 0 looks from +Z, the user's side of the deck. */
  theta: number;
  fit: Fit;
  /** Scales the fitted distance: above 1 stands further back. */
  zoom: number;
  /** Where the camera looks. A focus fit always looks at the region's centre. */
  target?: readonly [number, number, number];
}

/**
 * How much of the usable viewport the focus region spans, in whichever
 * direction runs out first. The record and the arm are the picture; the rest
 * of the deck gets what is left. The band the glass chrome leaves free is the
 * ceiling here: the 33/45 pads sit at the region's front edge, and any more
 * would put them under the now-playing bar.
 */
export const FOCUS_FILL = 0.94;

export const VIEWS: Record<ViewName, ViewPreset> = {
  top: { phi: 0.045, theta: 0, fit: 'focus', zoom: 1.0 },
  angle: { phi: 0.8, theta: 0.16, fit: 'deck', zoom: 1.12, target: [0, 3.2, 0] },
  front: { phi: 1.28, theta: 0.02, fit: 'deck', zoom: 1.22, target: [0, 4.6, 1.5] },
  arm: { phi: 0.62, theta: -0.62, fit: 'deck', zoom: 0.62, target: [10, 6.0, -5] },
};

/** The view the deck opens in, and the one a reset returns to. */
export const HOME_VIEW: ViewName = 'top';

/** A rectangle on a horizontal plane, which a view is fitted to. */
export interface Footprint {
  w: number;
  d: number;
  centre: readonly [number, number, number];
}

const FOOTPRINTS: Record<Fit, Footprint> = {
  deck: { w: DIM.deckW, d: DIM.deckD, centre: [0, DIM.top, 0] },
  focus: {
    w: FOCUS.maxX - FOCUS.minX,
    d: FOCUS.maxZ - FOCUS.minZ,
    centre: [(FOCUS.minX + FOCUS.maxX) / 2, FOCUS.y, (FOCUS.minZ + FOCUS.maxZ) / 2],
  },
};

export interface Insets {
  top: number;
  bottom: number;
}

/**
 * The vertical band of the viewport the glass chrome does not cover, measured
 * from the live layout so the deck's own controls stay reachable whatever the
 * HUD is doing.
 */
export function safeInsets(canvas: HTMLCanvasElement): Insets {
  const h = canvas.clientHeight || 1;
  const hud = document.querySelector<HTMLElement>('[data-hud]');
  if (!hud || hud.hidden) return { top: 0, bottom: 0 };

  const bar = document.querySelector<HTMLElement>('[data-topbar]');
  const np = document.querySelector<HTMLElement>('[data-nowplaying]');
  const top = bar ? Math.max(0, bar.getBoundingClientRect().bottom) + 8 : 0;
  const npRect = np?.getBoundingClientRect();
  const bottom = npRect?.height ? Math.max(0, h - npRect.top) + 8 : 0;
  return { top, bottom };
}

export interface FitContext {
  camera: THREE.PerspectiveCamera;
  canvas: HTMLCanvasElement;
  /** On portrait screens the deck turns a quarter turn to fill the screen. */
  layoutYaw: number;
}

/**
 * Perspective spreads the plinth's near edges, so a whole-deck fit needs more
 * headroom than the raw frustum maths suggests — and narrow screens need most.
 */
const deckFill = (aspect: number): number =>
  1 / (aspect < 0.62 ? 1.07 : aspect < 0.85 ? 1.34 : aspect < 1.2 ? 1.25 : 1.17);

/** Distance at which a footprint spans `fill` of the usable viewport. */
export function fitDistance(ctx: FitContext, fit: Fit, zoom = 1): number {
  const { camera, canvas, layoutYaw } = ctx;
  const fp = FOOTPRINTS[fit];
  const vFov = THREE.MathUtils.degToRad(camera.fov);
  const portrait = Math.abs(layoutYaw) > 0.5;
  const w = portrait ? fp.d : fp.w;
  const d = portrait ? fp.w : fp.d;

  const h = canvas.clientHeight || 1;
  const ins = safeInsets(canvas);
  const usable = clamp((h - ins.top - ins.bottom) / h, 0.45, 1);

  const forHeight = d / 2 / Math.tan(vFov / 2) / usable;
  const forWidth = w / 2 / (Math.tan(vFov / 2) * camera.aspect);
  const fill = fit === 'focus' ? FOCUS_FILL : deckFill(camera.aspect);
  return (Math.max(forHeight, forWidth) / fill) * zoom;
}

/** World-space direction that reads as "up" on screen for a given framing. */
export function screenUp(from: THREE.Vector3, target: THREE.Vector3): THREE.Vector3 {
  const fwd = new THREE.Vector3().subVectors(target, from).normalize();
  const up = new THREE.Vector3(0, 1, 0);
  up.addScaledVector(fwd, -up.dot(fwd));
  if (up.lengthSq() < 1e-8) up.set(0, 0, -1); /* exactly top-down */
  return up.normalize();
}

/** How far to lift the deck so it centres in the safe band, not the viewport. */
export function safeShift(ctx: FitContext, dist: number): number {
  const h = ctx.canvas.clientHeight || 1;
  const ins = safeInsets(ctx.canvas);
  const px = (ins.bottom - ins.top) / 2; /* > 0 means the deck should sit higher */
  if (Math.abs(px) < 1) return 0;
  const visible = 2 * dist * Math.tan(THREE.MathUtils.degToRad(ctx.camera.fov) / 2);
  return (px / h) * visible;
}

/** Where the camera and its target belong for a preset, safe area included. */
export function framingFor(
  ctx: FitContext,
  view: ViewPreset,
): { position: THREE.Vector3; target: THREE.Vector3 } {
  const dist = fitDistance(ctx, view.fit, view.zoom);
  const theta = view.theta + ctx.layoutYaw;
  const phi = clamp(view.phi, 0.001, 1.4);

  const aim =
    view.fit === 'focus' ? FOOTPRINTS.focus.centre : (view.target ?? FOOTPRINTS.deck.centre);
  const target = new THREE.Vector3(...aim);
  const position = new THREE.Vector3().setFromSphericalCoords(dist, phi, theta).add(target);

  /* Moving the camera one way moves the picture the other: to lift the deck
     into the band, the rig steps toward screen-down. */
  const shift = safeShift(ctx, dist);
  if (shift) {
    const down = screenUp(position, target).multiplyScalar(-shift);
    position.add(down);
    target.add(down);
  }
  return { position, target };
}
